import React, { useEffect, useRef, useState } from 'react';
import { loadPlaces, parsePlace, regionFromPostcode, normalizePostcode } from './places';
import { meter } from './gmaps-meter';
import { isValidContactPhone } from '../utils/identify';

// Postal code inside a Google `formatted_address`. Greek ΤΚ are 5 digits, often
// written "XXX XX" (e.g. "…, 104 32 Αθήνα"); Cypriot ΤΚ are a plain 4 digits
// (e.g. "…, 1010 Λευκωσία"). Returns the digits joined, or ''.
function postcodeFromFormatted(s, len) {
  const str = String(s || '');
  if (len === 4) {
    const m4 = str.match(/\b(\d{4})\b/);
    return m4 ? m4[1] : '';
  }
  const m = str.match(/\b(\d{3})\s?(\d{2})\b/);
  return m ? m[1] + m[2] : '';
}

// Google's autocomplete row and its own Place Details disagree about Greek
// municipalities: the dropdown offers "Υψηλάντου 17, Καλλιθέα" while Details for
// that exact place_id returns locality "Αθήνα" (and administrative_area_level_3
// "Νότιος Τομέας Αθηνών", the regional unit — not the δήμος either). Whole Athens
// municipalities — Καλλιθέα, Νέα Σμύρνη, Περιστέρι — collapse into "Αθήνα", which
// is the wrong δήμος on the courier label.
//
// The shopper chose the row, so the row is what we honour. The widget leaves that
// row's text in the input, and the segment before the country is the municipality.
// Returns '' when the shape isn't recognised, so the caller keeps Google's own
// locality rather than a guess.
const COUNTRY_WORDS = ['ελλάδα', 'greece', 'κύπρος', 'cyprus'];
export function municipalityFromDescription(desc) {
  const parts = String(desc == null ? '' : desc).split(',').map((s) => s.trim()).filter(Boolean);
  if (parts.length < 2) return '';
  if (COUNTRY_WORDS.indexOf(parts[parts.length - 1].toLowerCase()) !== -1) parts.pop();
  if (parts.length < 2) return ''; // need at least "street, city" — a bare locality tells us nothing new
  // Google sometimes trails a ΤΚ on the city ("Αθήνα 175 61"); keep the name only.
  return parts[parts.length - 1].replace(/\s*\d{3}\s?\d{2}\s*$/, '').trim();
}

const cfg = (typeof window !== 'undefined' && window.CHECKOUT_NEXT) || {};
const GR_STATES = cfg.grStates && typeof cfg.grStates === 'object' ? cfg.grStates : {};

// Which storefront is this — adaptoras.gr ('GR') or adaptoras.cy ('CY')? The
// server decides from the request host (see mu-plugins/36-cy-site.php) and hands
// it over in the boot payload; the whole address block keys off it rather than
// assuming Greece.
export const SITE_COUNTRY = cfg.siteCountry === 'CY' ? 'CY' : 'GR';

// Per-country address rules. Greek ΤΚ are 5 digits, Cypriot 4. phoneExample is what
// we show a shopper on that storefront — a Greek 10-digit example on adaptoras.cy was
// telling Cypriots to type a number their own country doesn't have.
export const COUNTRIES = {
  GR: { label: 'Ελλάδα', postcodeLen: 5, placesRegion: 'gr', zipExample: '10563', cityExample: 'Αθήνα', phoneExample: '6912345678' },
  CY: { label: 'Κύπρος', postcodeLen: 4, placesRegion: 'cy', zipExample: '1010', cityExample: 'Λευκωσία', phoneExample: '99123456' },
};

export const COUNTRY = COUNTRIES[SITE_COUNTRY];

export const EMPTY_ADDR = {
  first_name: '', last_name: '', company: '',
  address_1: '', address_2: '', city: '', postcode: '',
  state: '', country: SITE_COUNTRY, phone: '', email: '',
};

// ── Name fields ──────────────────────────────────────────────────────────────
// A person's name is letters plus the few separators real names carry: space,
// hyphen, apostrophe, and the dot of an initial. Greek, Latin (accents included)
// and Cyrillic are all allowed — a courier label has to be able to hold the
// customer's actual name, whatever alphabet it's in.
//
// The junk is stripped as it's typed rather than flagged afterwards: the field
// simply refuses a digit or a symbol, so nobody ever gets a filled-in form
// rejected at the Next button. Explicit ranges (not \p{L}) — a `/u` property
// escape is a parse error on older Safari, which would take the whole bundle
// down instead of just this field.
const NAME_CHARS = 'A-Za-z\\u00C0-\\u00D6\\u00D8-\\u00F6\\u00F8-\\u024F\\u0386\\u0388-\\u038A\\u038C\\u038E-\\u03A1\\u03A3-\\u03CE\\u0400-\\u04FF';
const NAME_STRIP = new RegExp(`[^${NAME_CHARS} '’.\\-]`, 'g');
const NAME_LETTER = new RegExp(`[${NAME_CHARS}]`, 'g');
export const NAME_MAX = 40;

export function cleanName(v) {
  return String(v == null ? '' : v)
    .replace(NAME_STRIP, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/^[\s'’.\-]+/, '') // a name never opens on a separator
    .slice(0, NAME_MAX);
}

// Usable name = two letters or more once the junk is gone. Deliberately judged
// on the cleaned value, so an address saved before this filter existed still
// passes instead of dead-ending a returning customer at checkout.
export function isValidName(v) {
  return (cleanName(v).match(NAME_LETTER) || []).length >= 2;
}

// Green "valid" tick shown on the inner-right of a control when its value is OK.
export function ValidTick() {
  return (
    <span className="cn-valid-tick" aria-hidden="true">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
    </span>
  );
}

// Single labelled text input. Pass `valid` to show the green tick, `invalid`
// to flag it red. Forwards `ref` to the <input> so callers can imperatively
// focus it (e.g. jumping to the next field after a modal closes).
export const Field = React.forwardRef(function Field({ label, value, onChange, type = 'text', maxLength, placeholder, autoComplete, inputMode, full, valid, invalid }, ref) {
  return (
    <label className={`cn-field ${full ? 'cn-field--full' : ''}`}>
      <span className="cn-field__label">{label}</span>
      <div className={`cn-field__control ${valid ? 'has-tick' : ''}`}>
        <input
          ref={ref}
          type={type}
          className={`cn-input ${invalid ? 'is-invalid' : ''}`}
          value={value || ''}
          maxLength={maxLength}
          placeholder={placeholder}
          autoComplete={autoComplete}
          inputMode={inputMode}
          onChange={(e) => onChange(e.target.value)}
        />
        {valid && <ValidTick />}
      </div>
    </label>
  );
});

// Input that starts locked (read-only, e.g. right after an autofill) with a
// pencil on the inner right that unlocks it for editing.
export function LockableField({ label, value, onChange, locked, onUnlock, full, inputMode, valid, invalid }) {
  return (
    <label className={`cn-field ${full ? 'cn-field--full' : ''}`}>
      <span className="cn-field__label">{label}</span>
      <div className={`cn-lockfield ${locked ? 'is-locked' : ''}`}>
        <input
          className={`cn-input ${invalid ? 'is-invalid' : ''}`}
          value={value || ''}
          readOnly={locked}
          inputMode={inputMode}
          onChange={(e) => onChange(e.target.value)}
        />
        {/* Locked → pencil (right slot); unlocked & valid → green tick. */}
        {locked ? (
          <button type="button" className="cn-lockfield__pencil" onClick={onUnlock} aria-label="Επεξεργασία" title="Επεξεργασία">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
          </button>
        ) : (valid && <ValidTick />)}
      </div>
    </label>
  );
}

// Address block matching the classic checkout: name, address (Google Places
// autocomplete), country, postcode, region (optional), city. No phone here —
// contact phone lives in its own section.
export function AddressFields({ value, onChange, locks, onUnlock, revealed = true, onReveal, invalids, showPhone = true, showName = true }) {
  const v = value || EMPTY_ADDR;
  const lk = locks || {};
  const bad = invalids || {};
  const unlock = onUnlock || (() => {});
  const set = (k) => (val) => onChange({ ...v, [k]: val });
  const setZip = (val) => set('postcode')(normalizePostcode(val));
  const digits = (s) => ((s || '').match(/\d/g) || []).length;
  const setName = (k) => (val) => onChange({ ...v, [k]: cleanName(val) });
  const ok = {
    first_name: isValidName(v.first_name),
    last_name: isValidName(v.last_name),
    address_1: (v.address_1 || '').trim() !== '',
    // Exact length for the storefront's country — 5 in Greece, 4 in Cyprus.
    postcode: digits(v.postcode) === COUNTRY.postcodeLen,
    city: (v.city || '').trim() !== '',
    phone: isValidContactPhone(v.phone, SITE_COUNTRY),
  };
  const pencil = (k) => (
    <button type="button" className="cn-lockfield__pencil" onClick={() => unlock(k)} aria-label="Επεξεργασία" title="Επεξεργασία">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
    </button>
  );

  // Keep latest value/onChange for the once-mounted autocomplete listener.
  const vRef = useRef(v);
  const onRef = useRef(onChange);
  const revealRef = useRef(onReveal);
  vRef.current = v;
  onRef.current = onChange;
  revealRef.current = onReveal;
  const inputRef = useRef(null);

  useEffect(() => {
    let ac;
    let cancelled = false;
    loadPlaces().then(() => {
      if (cancelled || !inputRef.current || !window.google) return;
      ac = new window.google.maps.places.Autocomplete(inputRef.current, {
        types: ['geocode'],
        componentRestrictions: { country: COUNTRY.placesRegion },
        fields: ['address_components', 'formatted_address', 'name', 'geometry'],
      });
      ac.addListener('place_changed', () => {
        const place = ac.getPlace();
        // Picking a suggestion makes Google fetch Place Details (address_components
        // and geometry aren't carried on a prediction) — that's the billable call.
        meter('places_details');
        const parsed = parsePlace(place, GR_STATES);
        // The coordinates arrive inside the Details response we have just paid
        // for. Carrying them into the draft is what lets the map centre itself
        // without a second, separately-billed forward geocode.
        const loc = place && place.geometry && place.geometry.location;
        if (loc) {
          const la = typeof loc.lat === 'function' ? loc.lat() : loc.lat;
          const ln = typeof loc.lng === 'function' ? loc.lng() : loc.lng;
          if (typeof la === 'number' && typeof ln === 'number') { parsed.lat = la; parsed.lng = ln; }
        }
        // The widget has just written the chosen row's text into the input, and
        // that row is the only place the real δήμος appears — Details will say
        // "Αθήνα" for an address the shopper picked as Καλλιθέα.
        const muni = municipalityFromDescription(inputRef.current && inputRef.current.value);
        if (muni) parsed.city = muni;
        // Always replace the address line with the selection — otherwise, when
        // Google returns no `route` (street w/o number, or an area), address_1
        // would stay the half-typed text and React would overwrite Google's DOM
        // value back to it. Fall back to the place name / formatted street.
        if (!parsed.address_1 && place) {
          const fallback = place.name || (place.formatted_address || '').split(',')[0] || '';
          if (fallback.trim()) parsed.address_1 = fallback.trim();
        }
        // Greece: Places often omits postal_code from address_components, leaving the
        // ΤΚ blank. Recover it from the formatted address first (cheap, synchronous).
        if (!parsed.postcode) {
          const pc = postcodeFromFormatted(place && place.formatted_address, COUNTRY.postcodeLen);
          // regionFromPostcode maps Greek ΤΚ prefixes to Greek regions — meaningless
          // for a Cypriot code, and WooCommerce has no CY states anyway.
          if (pc) { parsed.postcode = pc; if (!parsed.state && SITE_COUNTRY === 'GR') { const c = regionFromPostcode(pc); if (c) parsed.state = c; } }
        }
        onRef.current({ ...vRef.current, ...parsed });
        if (revealRef.current) revealRef.current(); // show the detail fields + map
        // Still no ΤΚ → reverse-geocode the picked location (reliable where the
        // formatted address carried no postcode). Merges into the latest draft.
        if (!parsed.postcode && place && place.geometry && place.geometry.location && window.google) {
          meter('geocoding');
          new window.google.maps.Geocoder().geocode({ location: place.geometry.location }, (results, status) => {
            if (status !== 'OK' || !Array.isArray(results)) return;
            let z = '';
            results.some((r) => {
              const c = (r.address_components || []).find((x) => x.types.indexOf('postal_code') !== -1);
              if (c) { z = normalizePostcode(c.long_name); return true; }
              return false;
            });
            if (!z) return;
            const patch = { postcode: z };
            if (!vRef.current.state) { const c = regionFromPostcode(z); if (c) patch.state = c; }
            onRef.current({ ...vRef.current, ...patch });
          });
        }
      });
    }).catch(() => { /* no key / offline — plain text input still works */ });
    return () => {
      cancelled = true;
      if (ac && window.google) window.google.maps.event.clearInstanceListeners(ac);
    };
  }, []);

  // Greek regions only — WooCommerce defines no states for Cyprus, so the
  // storefront there renders no region picker.
  const stateCodes = SITE_COUNTRY === 'GR' ? Object.keys(GR_STATES) : [];

  return (
    <div className="cn-addr">
      {showName && (
        <div className="cn-grid">
          <Field label="Όνομα" value={v.first_name} onChange={setName('first_name')} maxLength={NAME_MAX} autoComplete="given-name" placeholder="π.χ. Γιάννης" valid={ok.first_name} invalid={bad.first_name} />
          <Field label="Επίθετο" value={v.last_name} onChange={setName('last_name')} maxLength={NAME_MAX} autoComplete="family-name" placeholder="π.χ. Παπαδόπουλος" valid={ok.last_name} invalid={bad.last_name} />
        </div>
      )}

      <label className="cn-field cn-field--full">
        <span className="cn-field__label">Διεύθυνση</span>
        <div className={`cn-lockfield ${lk.address_1 ? 'is-locked' : ''}`}>
          <input
            ref={inputRef}
            type="text"
            className={`cn-input ${bad.address_1 ? 'is-invalid' : ''}`}
            value={v.address_1 || ''}
            readOnly={!!lk.address_1}
            autoComplete="off"
            placeholder="Ξεκίνα να πληκτρολογείς τη διεύθυνση…"
            onChange={(e) => set('address_1')(e.target.value)}
          />
          {lk.address_1 ? pencil('address_1') : (ok.address_1 && <ValidTick />)}
        </div>
      </label>

      {/* Phone only on the shipping address (billing_phone comes from the
          Επικοινωνία section). Sits right under the address. */}
      {showPhone && (
        <Field label="Τηλέφωνο παραλήπτη" value={v.phone} onChange={set('phone')} type="tel" inputMode="tel" autoComplete="tel" placeholder={`π.χ. ${COUNTRY.phoneExample}`} full valid={ok.phone} invalid={bad.phone} />
      )}

      {/* The rest stay hidden until an address is chosen, to keep entry uncluttered. */}
      {revealed && (
        <>
          <div className="cn-grid cn-grid--3">
            {/* One option by design: each storefront serves exactly one country
                (adaptoras.gr → Ελλάδα, adaptoras.cy → Κύπρος). Shown rather than
                hidden so the customer can see which country they're ordering in. */}
            <label className="cn-field">
              <span className="cn-field__label">Χώρα / Περιοχή</span>
              <select className="cn-input cn-select" value={v.country || SITE_COUNTRY} onChange={(e) => set('country')(e.target.value)}>
                <option value={SITE_COUNTRY}>{COUNTRY.label}</option>
              </select>
            </label>
            {lk.postcode
              ? <LockableField label="Ταχυδρομικός κώδικας" value={v.postcode} onChange={setZip} locked onUnlock={() => unlock('postcode')} inputMode="numeric" />
              // maxLength leaves room for the separator the browser's own autofill may
              // inject ("121 31") — setZip strips it, so the 5 digits survive intact.
              : <Field label="Ταχυδρομικός κώδικας" value={v.postcode} onChange={setZip} autoComplete="postal-code" inputMode="numeric" maxLength={COUNTRY.postcodeLen + 1} placeholder={`π.χ. ${COUNTRY.zipExample}`} valid={ok.postcode} invalid={bad.postcode} />}
            {stateCodes.length > 0 && (
              <label className="cn-field">
                <span className="cn-field__label">Περιφέρεια (προαιρετικό)</span>
                <select className="cn-input cn-select" value={v.state || ''} onChange={(e) => set('state')(e.target.value)}>
                  <option value="">—</option>
                  {stateCodes.map((code) => (
                    <option key={code} value={code}>{GR_STATES[code]}</option>
                  ))}
                </select>
              </label>
            )}
          </div>

          {lk.city
            ? <LockableField label="Πόλη / Κωμόπολη" value={v.city} onChange={set('city')} locked onUnlock={() => unlock('city')} full />
            : <Field label="Πόλη / Κωμόπολη" value={v.city} onChange={set('city')} autoComplete="address-level2" placeholder={`π.χ. ${COUNTRY.cityExample}`} full valid={ok.city} invalid={bad.city} />}
        </>
      )}
    </div>
  );
}

const addrLine = (f) => [f.address_1, f.postcode, f.city].filter(Boolean).join(', ');
const addrSub = (f) => [[f.first_name, f.last_name].filter(Boolean).join(' '), f.phone].filter(Boolean).join(' · ');

// Custom saved-address dropdown: each row shows the address on top + faded
// secondary line (name · phone) below. "Επεξεργασία" sits on the right of the
// selected row; the menu lists saved addresses + "Νέα διεύθυνση".
export function AddressPicker({ label, options, current, valueId, onSelect, onEdit, invalid }) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const cur = current || EMPTY_ADDR;
  const hasCurrent = !!(cur.address_1 || '').trim();
  const mainLine = hasCurrent ? addrLine(cur) : 'Επίλεξε ή πρόσθεσε διεύθυνση';
  const subLine = hasCurrent ? addrSub(cur) : '';

  // Rows: a "current" row if the selection is a custom/new address (not one of
  // the saved options), then every saved address. Each row is editable inline.
  const currentIsSaved = options.some((o) => o.id === valueId);
  const rows = [];
  if (hasCurrent && !currentIsSaved) rows.push({ id: null, fields: cur, current: true });
  options.forEach((o) => rows.push({ id: o.id, fields: o.fields, current: o.id === valueId }));

  return (
    <div className="cn-picker" ref={boxRef}>
      {label ? <span className="cn-picker__label">{label}</span> : null}
      <button type="button" className={`cn-picker__open ${invalid ? 'is-invalid' : ''}`} onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span className="cn-picker__text">
          <span className="cn-picker__main">{mainLine}</span>
          {subLine ? <span className="cn-picker__sub">{subLine}</span> : null}
        </span>
        <span className={`cn-picker__chevron ${open ? 'up' : ''}`} aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
        </span>
      </button>

      {open && (
        <ul className="cn-picker__menu">
          {rows.map((r, i) => (
            <li className={`cn-picker__li ${r.current ? 'is-current' : ''}`} key={r.id || 'current-' + i}>
              <button type="button" className="cn-picker__item" onClick={() => { if (r.id) onSelect(r.id); setOpen(false); }}>
                <span className="cn-picker__main">{addrLine(r.fields)}</span>
                {addrSub(r.fields) ? <span className="cn-picker__sub">{addrSub(r.fields)}</span> : null}
              </button>
              <button type="button" className="cn-picker__item-edit" onClick={(e) => { e.stopPropagation(); setOpen(false); onEdit(r.id); }}>
                Επεξεργασία
              </button>
            </li>
          ))}
          <li className="cn-picker__li">
            <button type="button" className="cn-picker__item cn-picker__item--new" onClick={() => { setOpen(false); onSelect('new'); }}>
              ➕ Νέα διεύθυνση…
            </button>
          </li>
        </ul>
      )}
    </div>
  );
}
